import type { ApiResponse } from "../../utils/apiClient";
import { adminApi, userApi } from "../../utils/apiInstances";

export type AuthContext = "admin" | "user";
type AuthPayload = Record<string, unknown>;

/**
 * Unified login handler for both Admin and User contexts.
 * @param userData Login credentials (email, password, etc.)
 * @param context "admin" or "user"
 * @param onSuccess Callback for successful login
 * @param onError Optional callback for failed login
 */
export const handleLogin = async (
    userData: AuthPayload,
    context: AuthContext,
    onSuccess: (response: ApiResponse) => void,
    onError?: (error: unknown) => void
) => {
    try {
        const api = context === "admin" ? adminApi : userApi;
        const endpoint = context === "admin" ? "/admin/auth/admin-login" : "/user/auth/login";

        const response = await api("POST", endpoint, userData);
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

/**
 * Ported User-specific Auth Functions
 */

export const handleUserRegister = async (
    userData: AuthPayload,
    ref: string | null,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const queryParams = ref ? `?ref=${encodeURIComponent(ref)}` : "";
        const response = await userApi(
            "POST",
            `/user/auth/register${queryParams}`,
            userData
        );
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

export const handleUserEmailVerify = async (
    userData: AuthPayload,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi(
            "POST",
            "/user/auth/email-verification",
            userData
        );
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

export const handleRegisteredEmailVerify = async (
    userData: AuthPayload,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi("POST", "/user/auth/verify-otp", userData);
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

export const handleResendOTPToMail = async (
    userData: AuthPayload,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi("POST", "/user/auth/send-otp", userData);
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

export const handleUserForgotPassword = async (
    userData: AuthPayload,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi("POST", "/user/auth/forgot-password", userData);
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

export const handleUserResetWaitOTP = async (
    userData: AuthPayload,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
) => {
    try {
        const response = await userApi(
            "POST",
            "/user/auth/reset-password-with-otp",
            userData
        );
        onSuccess(response);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};
